import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  CatalogSlug,
  type ContractService,
  errors,
  paxRoute,
  Sha256,
  StreamResponse,
  staffAuth,
} from "../../common";
import {
  EvalDetail,
  EvalDraft,
  EvalDraftDetails,
  EvalListing,
  EvalRun,
  EvalVersion,
  ResultPoint,
  RunDetail,
} from "./schemas";

export const service = {
  group: "evals",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Evals API",
  description: "Versioned evaluations and their games.",
} as const satisfies ContractService;

function endpoint(
  name: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: `/${string}`,
  summary: string,
  stream = false,
) {
  return oc
    .meta(
      paxRoute({
        auth: staffAuth("agents.manage"),
        minimumTier: method === "GET" ? null : "suspicious",
        cost: method === "GET" ? "read" : "write",
      }),
      openapi({
        method,
        path,
        operationId: `evals.${name}`,
        summary,
        inputStructure: "detailed",
        ...(stream ? { outputStructure: "detailed" as const } : {}),
      }),
    )
    .errors({
      ...errors.unauthorized,
      ...errors.forbidden,
      ...errors.notFound,
      ...errors.badRequest,
      ...errors.conflict,
    });
}
const params = z.object({ id: z.uuid() });
const root = "/api/evals/v1alpha1";
export const list = endpoint("list", "GET", root, "List evaluations.")
  .input(z.object({}))
  .output(z.object({ items: z.array(EvalListing) }));
export const get = endpoint("get", "GET", `${root}/{id}`, "Read an evaluation and its cases.")
  .input(z.object({ params, query: z.object({ versionId: z.uuid().optional() }) }))
  .output(EvalDetail);
export const create = endpoint("create", "POST", root, "Create an evaluation and its first draft.")
  .input(
    z.object({
      body: EvalDraft.extend({
        slug: CatalogSlug.optional().meta({
          description: "How tools such as the Lab name the evaluation. Generated when omitted.",
        }),
      }),
    }),
  )
  .output(z.object({ eval: EvalListing, draft: EvalDraftDetails }));
const draftParams = z.object({ draftId: z.uuid() });
export const createDraft = endpoint(
  "drafts.create",
  "POST",
  `${root}/{id}/drafts`,
  "Begin a draft of an evaluation.",
)
  .input(
    z.object({
      params,
      body: z.strictObject({
        from: z.uuid().nullable().meta({
          description:
            "The published version whose name, description, and cases the draft copies, or null to begin from nothing.",
        }),
      }),
    }),
  )
  .output(EvalDraftDetails);
export const saveDraft = endpoint(
  "drafts.set",
  "PUT",
  `${root}/drafts/{draftId}`,
  "Replace a draft's name, description, and cases.",
)
  .input(z.object({ params: draftParams, body: EvalDraft }))
  .output(EvalDraftDetails);
export const publishDraft = endpoint(
  "drafts.publish",
  "POST",
  `${root}/drafts/{draftId}/publish`,
  "Publish a draft as the evaluation's next version.",
)
  .input(z.object({ params: draftParams }))
  .output(EvalVersion);
const authorParams = params.extend({ userId: z.uuid() });
export const setAuthor = endpoint(
  "authors.set",
  "PUT",
  `${root}/{id}/authors/{userId}`,
  "Make someone an evaluation author. They must hold agents.manage.",
)
  .input(z.object({ params: authorParams }))
  .output(z.object({ success: z.boolean() }));
export const removeAuthor = endpoint(
  "authors.delete",
  "DELETE",
  `${root}/{id}/authors/{userId}`,
  "Stop someone being an evaluation author. The owner always remains one.",
)
  .input(z.object({ params: authorParams }))
  .output(z.object({ success: z.boolean() }));
export const archive = endpoint(
  "archive.set",
  "PUT",
  `${root}/{id}/archive`,
  "Archive an evaluation, or restore it.",
)
  .input(z.object({ params, body: z.strictObject({ archived: z.boolean() }) }))
  .output(EvalListing);
export const listRuns = endpoint("runs.list", "GET", `${root}/{id}/runs`, "List evaluation runs.")
  .input(z.object({ params }))
  .output(z.object({ items: z.array(EvalRun) }));
export const startRun = endpoint(
  "runs.create",
  "POST",
  `${root}/{id}/runs`,
  "Run an agent against a published evaluation.",
)
  .input(
    z.object({
      params,
      body: z.strictObject({
        evalVersionId: z.uuid(),
        agentVersionId: z.uuid(),
        repetitions: z.number().int().min(1).max(20),
        budgetCents: z.number().int().min(1).max(45000),
        idempotencyKey: z.uuid(),
      }),
    }),
  )
  .output(EvalRun);
export const getRun = endpoint("runs.get", "GET", `${root}/runs/{id}`, "Read a run and its games.")
  .input(z.object({ params }))
  .output(RunDetail);
export const cancelRun = endpoint(
  "runs.cancel",
  "POST",
  `${root}/runs/{id}/cancel`,
  "Cancel an evaluation run.",
)
  .input(z.object({ params }))
  .output(EvalRun);

const Notes = z.strictObject({ notes: z.string().max(100_000) });

export const setRunNotes = endpoint(
  "runs.notes.set",
  "PUT",
  `${root}/runs/{id}/notes`,
  "Write a run's notes. The run's owner and the evaluation's authors may.",
)
  .input(z.object({ params, body: Notes }))
  .output(EvalRun);

export const setCaseNotes = endpoint(
  "cases.notes.set",
  "PUT",
  `${root}/{id}/cases/{caseKey}/notes`,
  "Write a case's notes, which every version of the evaluation shares. Its authors may.",
)
  .input(z.object({ params: params.extend({ caseKey: z.string().min(1).max(100) }), body: Notes }))
  .output(z.object({ success: z.boolean() }));

export const listResults = endpoint(
  "results.list",
  "GET",
  `${root}/results`,
  "Compare measured evaluation results.",
)
  .input(z.object({}))
  .output(z.object({ items: z.array(ResultPoint) }));

export const listWorlds = endpoint(
  "worlds.list",
  "GET",
  `${root}/worlds`,
  "Search scenarios and games you can open.",
)
  .input(z.object({ query: z.object({ q: z.string().max(200).default("") }) }))
  .output(
    z.object({
      items: z.array(
        z.object({
          id: z.uuid(),
          kind: z.enum(["preset", "game"]),
          title: z.string(),
          presetVersionId: z.uuid(),
          revision: z.number().nullable(),
        }),
      ),
    }),
  );
export const getStartingWorld = endpoint(
  "startingWorld.get",
  "GET",
  `${root}/starting-world`,
  "Read the perspectives in a starting world.",
)
  .input(
    z.object({
      query: z.object({
        presetVersionId: z.uuid(),
        gameId: z.uuid().optional(),
        revision: z.coerce.number().int().positive().optional(),
        world: Sha256.optional().meta({ description: "An uploaded world to read instead." }),
      }),
    }),
  )
  .output(
    z.object({
      revision: z.number(),
      perspectives: z.array(
        z.object({ id: z.string(), name: z.string(), heldBy: z.array(z.string()) }),
      ),
    }),
  );

export const setWorld = oc
  .meta(
    paxRoute({ auth: staffAuth("agents.manage"), minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: `${root}/worlds/{sha256}`,
      operationId: "evals.worlds.set",
      summary: "Upload a starting world.",
      description:
        "Stores a world file under its SHA-256 so a case can start from it with `startingWorld`. The file holds one delta per line and seats nobody. Uploading the same file again returns it unchanged.",
      inputStructure: "detailed",
      requestBodyHint: "octet-stream",
      successDescription: "The stored world and its last revision.",
    }),
  )
  .errors({ ...errors.unauthorized, ...errors.forbidden, ...errors.badRequest })
  .input(z.object({ params: z.object({ sha256: Sha256 }), body: z.instanceof(ReadableStream) }))
  .output(z.object({ sha256: Sha256, revision: z.number().int().positive() }));

export const listAuthorCandidates = endpoint(
  "authorCandidates.list",
  "GET",
  `${root}/author-candidates`,
  "Find people who can author agents or evaluations, by username.",
)
  .input(z.object({ query: z.object({ q: z.string().min(2).max(25) }) }))
  .output(
    z.object({
      items: z.array(z.object({ id: z.uuid(), name: z.string(), username: z.string() })),
    }),
  );

export const getCaseWorld = endpoint(
  "versions.cases.world.get",
  "GET",
  `${root}/versions/{versionId}/cases/{caseId}/world`,
  "Download a case's immutable starting world.",
  true,
)
  .input(
    z.object({ params: z.object({ versionId: z.uuid(), caseId: z.string().min(1).max(100) }) }),
  )
  .output(StreamResponse);

export const getGameWorld = endpoint(
  "games.world.get",
  "GET",
  `${root}/games/{id}/world`,
  "Download an evaluation game's record, or only the deltas committed after a revision.",
  true,
)
  .input(
    z.object({
      params,
      query: z.object({
        after: z.coerce
          .number()
          .int()
          .nonnegative()
          .default(0)
          .meta({ description: "Only deltas after this revision; the whole record when 0." }),
      }),
    }),
  )
  .output(StreamResponse);
